import { useMemo, useState } from "react";

interface Ticket {
  created_at: string;
  status: string;
  priority?: string;
  updated_at?: string;
  closed_at?: string | null;
}

interface TicketResolutionChartProps {
  tickets: Ticket[];
}

function startOfWeek(d: Date) {
  const dt = new Date(d);
  const day = dt.getDay(); // 0 (Sun) - 6
  const diff = (day + 6) % 7; // convert so Monday => 0
  dt.setDate(dt.getDate() - diff);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

export default function TicketResolutionChart({ tickets }: TicketResolutionChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);


  const data = useMemo(() => {
    // Build last 12 weeks window buckets based on created_at (opened)
    const now = new Date();
    const currentWeekStart = startOfWeek(now);

    const buckets = Array.from({ length: 12 }, (_, idx) => {
      const weekStart = new Date(currentWeekStart);
      weekStart.setDate(weekStart.getDate() - (11 - idx) * 7);
      const label = `W${idx + 1}`;
      return {
        week: label,
        start: weekStart,
        opened: 0,
        resolved: 0,
        resolutionHoursSum: 0,
        resolutionCount: 0,
      };
    });

    const weekEndTimes = buckets.map((b) => new Date(new Date(b.start).getTime() + 7 * 24 * 60 * 60 * 1000).getTime());

    const resolvedStatuses = new Set(["closed", "resolved", "completed"]);

    for (const t of tickets) {
      const created = t.created_at ? new Date(t.created_at) : null;
      if (!created || Number.isNaN(created.getTime())) continue;
      const createdTs = created.getTime();

      // opened bucket
      const bucketIndex = weekEndTimes.findIndex((endTs) => createdTs < endTs);
      if (bucketIndex === -1) continue;

      buckets[bucketIndex].opened += 1;

      // resolved bucket + avg resolution (we approximate resolution time if we have updated_at/closed_at)
      const isResolved = resolvedStatuses.has(t.status);
      if (isResolved) {
        buckets[bucketIndex].resolved += 1;
        const endIso = t.closed_at || t.updated_at;
        if (endIso) {
          const end = new Date(endIso);
          if (!Number.isNaN(end.getTime())) {
            const hours = (end.getTime() - createdTs) / (1000 * 60 * 60);
            if (Number.isFinite(hours) && hours >= 0) {
              buckets[bucketIndex].resolutionHoursSum += hours;
              buckets[bucketIndex].resolutionCount += 1;
            }
          }
        }
      }
    }

    return buckets.map((b) => {
      const avgResolutionHrs = b.resolutionCount > 0 ? b.resolutionHoursSum / b.resolutionCount : 0;
      return { week: b.week, opened: b.opened, resolved: b.resolved, avgResolutionHrs };
    });
  }, [tickets]);

  const maxVal = Math.max(1, ...data.map((d) => Math.max(d.opened, d.resolved)));
  const totalOpened = data.reduce((s, d) => s + d.opened, 0);
  const totalResolved = data.reduce((s, d) => s + d.resolved, 0);
  const resolutionRate = totalOpened > 0 ? Math.round((totalResolved / totalOpened) * 100) : 0;
  const avgResolutionHrs = data.reduce((s, d) => s + d.avgResolutionHrs, 0) / (data.length || 1);

  const getH = (val: number) => Math.max(4, (val / maxVal) * 100);


  return (
    <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-dark-border">
        <div>
          <h2 className="text-white font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Ticket Resolution Trends
          </h2>
          <p className="text-silver-dim text-xs mt-0.5">Opened vs resolved over time</p>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-cyber-green font-semibold bg-cyber-green/10 border border-cyber-green/20 px-2.5 py-1 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-cyber-green" />
          {resolutionRate}% resolved
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-3 divide-x divide-dark-border border-b border-dark-border">
        <div className="px-4 py-3">
          <p className="text-silver-dim text-xs">Total Opened</p>
          <p className="text-white text-lg font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{totalOpened}</p>
        </div>
        <div className="px-4 py-3">
          <p className="text-silver-dim text-xs">Total Resolved</p>
          <p className="text-cyber-green text-lg font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{totalResolved}</p>
        </div>
        <div className="px-4 py-3">
          <p className="text-silver-dim text-xs">Avg Resolution</p>
          <p className="text-cyber-orange text-lg font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{avgResolutionHrs.toFixed(1)}h</p>
        </div>
      </div>

      {/* Chart */}
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-end gap-2 h-36">
          {data.map((d, i) => {
            const isHovered = hoveredIndex === i;
            const openedH = getH(d.opened);
            const resolvedH = getH(d.resolved);
            return (
              <div
                key={d.week}
                className="flex-1 flex flex-col items-center gap-1 cursor-pointer"
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div className="flex items-end gap-0.5 w-full" style={{ height: "100px" }}>
                  <div
                    className="flex-1 rounded-t-sm transition-all duration-200"
                    style={{
                      height: `${openedH}%`,
                      background: isHovered ? "#FF4444" : "#FF444455",
                    }}
                  />
                  <div
                    className="flex-1 rounded-t-sm transition-all duration-200"
                    style={{
                      height: `${resolvedH}%`,
                      background: isHovered ? "#00FF88" : "#00FF8855",
                    }}
                  />
                </div>
                <span className="text-silver-dim text-xs">{d.week}</span>
              </div>
            );
          })}
        </div>
        {/* Legend */}
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-dark-border">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-cyber-red/60" />
            <span className="text-silver-dim text-xs">Opened</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-cyber-green/60" />
            <span className="text-silver-dim text-xs">Resolved</span>
          </div>
        </div>
      </div>
    </div>
  );
}
